// The sea mine marks of the Marks field `seaMine` (v2-the-deep.md §11.8,
// mockup round 069, R2538, R2652).
//
// One look draws all four kinds of §11.2: the Barb, the Bola, the Siren and
// the Petard. Each look ports its card of
// `archive/mockups/069-sea-mines.html` point for point. A glyph draws ONE
// sea mine at the origin, +y down, one unit one map px, in the side ink the
// caller hands it. Every look draws the two states the arm rule needs (an
// unarmed sea mine is hollow, dashed or stubbed) and the hostile tell.
//
// The AUTO look `overprint-restricted` is cards 12 and 14 as one look: it
// follows the chart reading, card 12 Wartime Overprint for the historical
// reading and card 14 Restricted Area for the modern one and for `off`.
// Both cards also draw an AREA round the armed sea mines of each side
// (`paintSeaMineArea`).
//
// The figures a card draws to scale (the contact radius and the splash of
// a kind) are pack figures, so the caller passes them in `SeaMineGlyph`.
// The client lane that draws sea objects on the map reads them from the
// catalog. `seaobject-cells.ts` bakes the looks that hold still into atlas
// cells; `living-motion` and `drogue-lines` move, so the layer paints them
// each frame, or bakes them at t = 0 with the motion off.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { SEA_MINE_STYLES } from '../gen/balance';
import { SeaObjectKind } from '../gen/proto/aquaphobia';
import { seamarks as WORDS } from '../gen/strings';
import {
  D, Fl, P, S, SEA_INK, arc, bandPx, brackets, chevrons, circle, disc, dots, line, padHull,
  text, ticks, topOf, walkPoly, wordFont, type Ctx, type Pt,
} from './seaobject-kit';
import { TAU } from './math';

/** One look of the `seaMine` field. */
export type SeaMineStyle = (typeof SEA_MINE_STYLES)[number];

/** The four kinds of §11.2, by the slug of the round page. */
export type SeaMineKind = 'barb' | 'bola' | 'siren' | 'petard';
export const SEA_MINE_KINDS: readonly SeaMineKind[] = ['barb', 'bola', 'siren', 'petard'];

/** The wire kind of each sea mine kind (`SeaObjectKind`, R2644). */
export const SEA_MINE_KIND_OF: ReadonlyMap<SeaObjectKind, SeaMineKind> = new Map([
  [SeaObjectKind.SEA_OBJECT_KIND_BARB, 'barb'],
  [SeaObjectKind.SEA_OBJECT_KIND_BOLA, 'bola'],
  [SeaObjectKind.SEA_OBJECT_KIND_SIREN, 'siren'],
  [SeaObjectKind.SEA_OBJECT_KIND_PETARD, 'petard'],
] as [SeaObjectKind, SeaMineKind][]);

/** The chart reading the AUTO look follows (the CHART setting). */
export type SeaChartReading = 'historical' | 'modern' | 'off';

/** What one glyph is told about the sea mine it draws. */
export interface SeaMineGlyph {
  /** The side ink. A cell paints white and the layer tints it. */
  col: number;
  /** False until the sea mine arms (§11.2 rule 2). */
  armed: boolean;
  /** True for a sea mine of another faction. */
  hostile: boolean;
  /** The clock in seconds, for a look that moves. */
  t: number;
  /** The phase of this sea mine, 0 to 1: one sea mine moves apart from its
   *  neighbour. */
  ph: number;
  /** The screen px per map px where the glyph draws: a hairline keeps 0.8
   *  screen px at least. */
  s: number;
  /** The heading of the current at the sea mine, in radians. */
  da: number;
  /** False where the player turned the motion off. */
  motion: boolean;
  /** The contact radius of the kind, map px (seaobjects.toml). */
  contact: number;
  /** The splash of the kind, map px; 0 for a kind with none. */
  splash: number;
  /** The chart reading, which the AUTO look follows. */
  reading: SeaChartReading;
}

/** A stroke width that keeps 0.8 screen px at least. */
const W = (o: SeaMineGlyph, w: number): number => Math.max(w, 0.8 / o.s);

type Glyph = (c: Ctx, k: SeaMineKind, o: SeaMineGlyph) => void;

const DEEP = SEA_INK.deep;

// ---- the physical object -----------------------------------------------------

/** Card 01 Horned Sphere: a ball with knobbed horns, seen from above. */
const horned: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.8);
  const ball = (x: number, y: number, r: number, angs: readonly number[], hl: number): void => {
    if (A) {
      disc(c, x, y, r, col, 0.92);
      for (const d of angs) {
        const a = D(d), ca = Math.cos(a), sa = Math.sin(a);
        line(c, x + ca * r, y + sa * r, x + ca * (r + hl), y + sa * (r + hl), col, 0.95, w);
        disc(c, x + ca * (r + hl), y + sa * (r + hl), 0.6, col, 1);
      }
    } else {
      circle(c, x, y, r, col, 0.9, w, [1.2, 0.9]); disc(c, x, y, 0.7, col, 0.8);
      for (const d of angs) {
        const a = D(d), ca = Math.cos(a), sa = Math.sin(a);
        line(c, x + ca * (r + 0.3), y + sa * (r + 0.3), x + ca * (r + 0.9), y + sa * (r + 0.9), col, 0.9, w);
      }
    }
  };
  if (k === 'barb') ball(0, 0, 2.6, [-90, -30, 30, 90, 150, 210], 1.6);
  else if (k === 'bola') {
    line(c, -1.8, 0, 1.8, 0, col, A ? 0.85 : 0.5, w);
    ball(-3.6, 0, 1.8, [120, 180, 240], 1.3); ball(3.6, 0, 1.8, [-60, 0, 60], 1.3);
  } else if (k === 'siren') {
    ball(0, 0, 2.4, [], 0);
    if (A) {
      circle(c, 0, 0, 4.4, col, 0.85, w);
      for (const d of [-90, 30, 150]) disc(c, Math.cos(D(d)) * 4.4, Math.sin(D(d)) * 4.4, 0.95, col, 1);
    } else dots(c, 0, 0, 4.4, col, 0.85, W(o, 0.9), 1.4);
  } else {
    ball(0, 0, 3.9, [-90, -45, 0, 45, 90, 135, 180, 225], 1.7);
    if (A) circle(c, 0, 0, 2.2, DEEP, 0.75, W(o, 0.7));
  }
  if (o.hostile) brackets(c, { barb: 6.6, bola: 7.8, siren: 6.6, petard: 8.4 }[k], col, 0.95, w, 2.1);
};

/** Card 03 Drogue Lines: a float on a line with a drogue cup, the line down
 *  the current. The line moves, so the layer paints it each frame. */
const drogue: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.75), L = k === 'petard' ? 3.2 : 6.4, t = o.motion ? o.t : 0;
  c.save(); c.rotate(o.da);
  const cup = (x0: number, y0: number): void => {
    const wob = (u: number): number => (A ? Math.sin(t * 3 + o.ph * 9 + u * 4) * 0.4 * u : Math.sin(u * Math.PI * 3) * 1.0);
    c.beginPath();
    for (let i = 0; i <= 12; i++) {
      const u = i / 12;
      if (i) c.lineTo(x0 + u * L, y0 + wob(u)); else c.moveTo(x0, y0 + wob(0));
    }
    S(c, col, 0.8, w, A ? [] : [0.8, 0.8]);
    const ex = x0 + L, ey = y0 + wob(1);
    if (A) arc(c, ex + 0.5, ey, 1.4, -Math.PI / 2, Math.PI / 2, col, 0.95, W(o, 0.9));
    else disc(c, ex, ey, 0.6, col, 0.8);
  };
  const fl = (x: number, y: number, r: number): void => {
    if (A) disc(c, x, y, r, col, 0.95); else circle(c, x, y, r, col, 0.9, w);
  };
  if (k === 'barb') {
    cup(2.2, 0); fl(0, 0, 2.2); line(c, 0, -2.2, 0, -3.8, col, 0.95, w); line(c, 0, 2.2, 0, 3.8, col, 0.95, w);
  } else if (k === 'bola') {
    for (const y of [-2.1, 2.1]) { cup(1.5, y); fl(0, y, 1.5); }
    line(c, 0, -0.6, 0, 0.6, col, 0.9, w);
  } else if (k === 'siren') {
    cup(2.3, 0); fl(0, 0, 2.2); arc(c, 0.6, 0, 4, D(140), D(220), col, 0.9, W(o, 0.9));
  } else {
    cup(3.2, 0); c.beginPath(); c.rect(-3.2, -3.2, 6.4, 6.4);
    if (A) Fl(c, col, 0.95); else S(c, col, 0.9, w);
  }
  c.restore();
  if (o.hostile) circle(c, 0, 0, k === 'petard' ? 6.8 : 6, col, 0.95, W(o, 0.85), [1.6, 1.1]);
};

/** Card 04 Drums and Grilles: a canister seen from above. */
const drums: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.9), dash = A ? [] : [1.1, 0.9];
  if (k === 'barb') { circle(c, 0, 0, 2.8, col, 0.95, w, dash); if (A) disc(c, 0, 0, 1.5, col, 0.95); }
  else if (k === 'bola') {
    c.beginPath(); c.rect(-3, -0.8, 6, 1.6);
    if (A) Fl(c, col, 0.8); else S(c, col, 0.7, W(o, 0.6));
    for (const x of [-3.2, 3.2]) {
      if (A) { disc(c, x, 0, 1.9, col, 0.95); disc(c, x, 0, 0.7, DEEP, 0.8); } else circle(c, x, 0, 1.9, col, 0.9, w, dash);
    }
  } else if (k === 'siren') {
    circle(c, 0, 0, 3.2, col, 0.95, w, dash);
    if (A) disc(c, 0, 0, 3.2, col, 0.25);
    for (const x of [-1.3, 0, 1.3]) {
      const hh = Math.sqrt(3.2 * 3.2 - x * x) - 0.7;
      line(c, x, -hh, x, hh, col, 0.95, W(o, 0.7));
    }
  } else {
    circle(c, 0, 0, 4.4, col, 0.95, W(o, 1.3), dash); circle(c, 0, 0, 3, col, 0.85, W(o, 0.8), dash);
    if (A) disc(c, 0, 0, 1.8, col, 0.95);
  }
  if (o.hostile) {
    const r = k === 'petard' ? 7.2 : k === 'bola' ? 7 : 6;
    for (let i = 0; i < 4; i++) arc(c, 0, 0, r, D(i * 90 + 18), D(i * 90 + 72), col, 0.9, W(o, 0.8));
    ticks(c, r - 1.6, r + 1.6, col, 0.95, W(o, 0.8), 4, 0);
  }
};

/** Card 06 Seed Burrs: a seed that catches, a shape from the living sea. */
const burrs: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed;
  const burr = (x: number, y: number, r: number, n: number, len: number, hook: boolean, wd: number): void => {
    if (!A) {
      circle(c, x, y, r, col, 0.9, W(o, 0.7));
      for (let i = 0; i < n; i++) {
        const a = (i * TAU) / n + 0.3;
        line(c, x + Math.cos(a) * (r + 0.4), y + Math.sin(a) * (r + 0.4), x + Math.cos(a) * (r + 0.9), y + Math.sin(a) * (r + 0.9), col, 0.85, W(o, 0.5));
      }
      return;
    }
    disc(c, x, y, r, col, 0.95);
    for (let i = 0; i < n; i++) {
      const a = (i * TAU) / n + 0.3, ca = Math.cos(a), sa = Math.sin(a), ex = x + ca * (r + len), ey = y + sa * (r + len);
      line(c, x + ca * r, y + sa * r, ex, ey, col, 0.95, W(o, wd));
      if (hook) { const b = a + 2.2; line(c, ex, ey, ex + Math.cos(b) * 0.8, ey + Math.sin(b) * 0.8, col, 0.95, W(o, wd)); }
    }
  };
  if (k === 'barb') burr(0, 0, 1.8, 10, 2.2, true, 0.6);
  else if (k === 'bola') { burr(-1.5, 0, 1.5, 7, 1.7, true, 0.55); burr(1.5, 0, 1.5, 7, 1.7, true, 0.55); }
  else if (k === 'siren') {
    if (A) {
      disc(c, 0, 0, 3, col, 0.95);
      for (let i = 0; i < 7; i++) { const a = (i * TAU) / 7; line(c, 0, 0, Math.cos(a) * 2.4, Math.sin(a) * 2.4, DEEP, 0.75, W(o, 0.5)); }
    } else circle(c, 0, 0, 3, col, 0.9, W(o, 0.7));
    dots(c, 0, 0, 3.9, col, 0.85, W(o, 0.7), 1.25);
  } else {
    burr(0, 0, 4, 9, 1.5, false, 1.0);
    if (A) arc(c, 0, 0, 2.4, D(200), D(340), DEEP, 0.7, W(o, 0.7));
  }
  if (o.hostile) {
    const r = k === 'petard' ? 8 : k === 'bola' ? 6.8 : 6.2;
    arc(c, 0, 0, r, D(130), D(230), col, 0.95, W(o, 0.9)); arc(c, 0, 0, r, D(-50), D(50), col, 0.95, W(o, 0.9));
  }
};

/** Card 07 Side Profile: the side view of the object, as an icon book draws
 *  it. It never turns. */
const profile: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.8);
  const body = (fn: () => void): void => { fn(); if (A) Fl(c, col, 0.95); else S(c, col, 0.9, w); };
  if (k === 'barb') {
    body(() => { c.beginPath(); c.arc(0, 0.6, 2.6, 0, TAU); });
    if (A) {
      for (const d of [-160, -125, -90, -55, -20]) {
        const a = D(d);
        line(c, Math.cos(a) * 2.6, 0.6 + Math.sin(a) * 2.6, Math.cos(a) * 4.1, 0.6 + Math.sin(a) * 4.1, col, 0.95, w);
      }
    }
    line(c, -1.4, 3.9, 1.4, 3.9, col, 0.9, w);
  } else if (k === 'bola') {
    c.beginPath(); c.arc(0, 1.4, 3.2, Math.PI, TAU); S(c, col, 0.85, w, A ? [] : [0.8, 0.8]);
    for (const x of [-3.2, 3.2]) body(() => { c.beginPath(); c.arc(x, 2.6, 1.6, 0, TAU); });
  } else if (k === 'siren') {
    body(() => { c.beginPath(); c.arc(0, 1.6, 2.4, 0, TAU); });
    body(() => { P(c, [[-0.5, -0.7], [0.5, -0.7], [2.1, -3.9], [-2.1, -3.9]]); });
  } else {
    body(() => { c.beginPath(); c.roundRect(-4, -3, 8, 6.4, 1.6); });
    const hc = A ? DEEP : col, ha = A ? 0.7 : 0.6;
    line(c, -1.6, -3, -1.6, 3.4, hc, ha, W(o, 0.7)); line(c, 1.6, -3, 1.6, 3.4, hc, ha, W(o, 0.7));
  }
  if (o.hostile) chevrons(c, k === 'petard' ? 7.4 : 6.6, 4, D(45), col, 0.95, W(o, 0.9), 1.5);
};

// ---- the old printed chart -----------------------------------------------------

/** Card 08 Copperplate: the old engraved chart, a hatched sphere with
 *  tapered horns. */
const copperplate: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.65);
  const sph = (x: number, y: number, r: number): void => {
    circle(c, x, y, r, col, 0.95, w);
    if (!A) return;
    c.save(); c.beginPath(); c.arc(x, y, r, 0, TAU); c.clip();
    for (let d = -r * 2; d <= r * 2; d += 0.95) line(c, x + d - r, y + r, x + d + r, y - r, col, 0.75, W(o, 0.42));
    c.restore();
  };
  const horn = (r: number, d: number, len: number): void => {
    const a = D(d), ca = Math.cos(a), sa = Math.sin(a);
    P(c, [[ca * r - sa * 0.55, sa * r + ca * 0.55], [ca * (r + len), sa * (r + len)], [ca * r + sa * 0.55, sa * r - ca * 0.55]]);
    if (A) Fl(c, col, 0.95); else S(c, col, 0.9, W(o, 0.45));
  };
  if (k === 'barb') { sph(0, 0, 2.8); for (const d of [-45, 45, 135, 225]) horn(2.8, d, 1.8); }
  else if (k === 'bola') {
    line(c, -1.5, -0.5, 1.5, -0.5, col, 0.9, W(o, 0.45)); line(c, -1.5, 0.5, 1.5, 0.5, col, 0.9, W(o, 0.45));
    sph(-3.2, 0, 1.8); sph(3.2, 0, 1.8);
  } else if (k === 'siren') {
    sph(-0.8, 0, 2.6);
    [3.5, 4.7, 5.9].forEach((r, i) => arc(c, -0.8, 0, r, D(-35), D(35), col, 0.9 - i * 0.2, W(o, 0.55), A ? [] : [0.7, 0.7]));
  } else {
    sph(0, 0, 4); circle(c, 0, 0, 4.8, col, 0.8, W(o, 0.45));
    for (const d of [0, 45, 90, 135, 180, 225, 270, 315]) horn(4.8, d, 1.5);
  }
  if (o.hostile) {
    const r = k === 'petard' ? 8.6 : k === 'bola' ? 7.6 : 7;
    for (let i = 0; i < 12; i++) {
      const a = (i * Math.PI) / 6;
      disc(c, Math.cos(a) * r, Math.sin(a) * r, Math.max(0.5, 0.5 / o.s), col, 0.95);
    }
  }
};

/** Card 09 Danger Circle: the dotted danger line at the true contact radius,
 *  and the splash as a second circle. Only an armed sea mine draws it. */
const dangerCircle: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.9), R = o.contact;
  if (k === 'barb') {
    if (A) { line(c, -2.2, 0, 2.2, 0, col, 0.95, w); line(c, 0, -2.2, 0, 2.2, col, 0.95, w); }
    else {
      for (const s of [[-2.2, 0, -0.8, 0], [0.8, 0, 2.2, 0], [0, -2.2, 0, -0.8], [0, 0.8, 0, 2.2]]) line(c, s[0], s[1], s[2], s[3], col, 0.9, w);
    }
  } else if (k === 'bola') {
    line(c, -1.7, 0, 1.7, 0, col, 0.8, W(o, 0.6));
    for (const x of [-1.7, 1.7]) { if (A) disc(c, x, 0, 1, col, 1); else circle(c, x, 0, 1, col, 0.9, W(o, 0.6)); }
  } else if (k === 'siren') {
    if (A) disc(c, 0, 0, 1.1, col, 1); else circle(c, 0, 0, 1.1, col, 0.9, W(o, 0.6));
    arc(c, 0, 0, 2.6, D(-40), D(40), col, 0.9, W(o, 0.7)); arc(c, 0, 0, 2.6, D(140), D(220), col, 0.9, W(o, 0.7));
  } else if (A) disc(c, 0, 0, 2.4, col, 1);
  else circle(c, 0, 0, 2.4, col, 0.9, w);
  if (A) {
    dots(c, 0, 0, R, col, 0.85, W(o, 1), 2.1);
    if (k === 'petard') dots(c, 0, 0, o.splash, col, 0.45, W(o, 0.9), 3.4);
    if (k === 'siren') dots(c, 0, 0, o.splash, col, 0.35, W(o, 0.8), 3.4);
  }
  if (o.hostile) { const r = A ? R : 5.4, d = A ? 2.2 : 1.5; ticks(c, r - d, r + d, col, 0.95, W(o, 1), 4, 0); }
};

/** Card 10 Foul Ground: the old chart's marks for a danger under water. */
const foulGround: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.85), dash = A ? [] : [0.7, 0.8];
  const plus = (x: number, y: number, s: number, wd: number): void => {
    line(c, x - s, y, x + s, y, col, 0.95, W(o, wd), dash); line(c, x, y - s, x, y + s, col, 0.95, W(o, wd), dash);
  };
  const wave = (y: number): void => {
    c.beginPath();
    for (let i = 0; i <= 16; i++) {
      const x = -3 + (i * 6) / 16;
      if (i) c.lineTo(x, y + Math.sin((i / 16) * Math.PI * 3) * 0.9); else c.moveTo(x, y);
    }
    S(c, col, 0.95, W(o, 0.9), dash);
  };
  if (k === 'barb') {
    plus(0, 0, 2.5, 0.85);
    if (A) for (const [qx, qy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) disc(c, qx * 1.5, qy * 1.5, 0.5, col, 1);
  } else if (k === 'bola') {
    plus(-2.5, -0.6, 1.5, 0.8); plus(2.5, -0.6, 1.5, 0.8);
    c.beginPath(); c.moveTo(-2.5, 1); c.quadraticCurveTo(0, 3.2, 2.5, 1); S(c, col, 0.9, w, dash);
  } else if (k === 'siren') { wave(-1.3); wave(1.3); }
  else {
    for (const v of [-1.3, 1.3]) {
      line(c, v, -3.2, v, 3.2, col, 0.95, W(o, 1.1), dash); line(c, -3.2, v, 3.2, v, col, 0.95, W(o, 1.1), dash);
    }
  }
  if (o.hostile) {
    const r = k === 'bola' ? 5.8 : 5.2;
    for (let i = 0; i <= 4; i++) {
      const v = -r + (i * r) / 2;
      for (const [qx, qy] of [[v, -r], [v, r], [-r, v], [r, v]]) disc(c, qx, qy, Math.max(0.45, 0.55 / o.s), col, 0.95);
    }
  }
};

/** Card 12 Wartime Overprint: an inked cross, one for each kind. */
const overprint: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, dash = A ? [] : [1.1, 0.8], j = (o.ph - 0.5) * 0.3;
  const cross = (x: number, y: number, s: number, wd: number): void => {
    c.save(); c.translate(x, y); c.rotate(j);
    if (A) { line(c, -s, -s, s, s, col, 0.95, W(o, wd)); line(c, s, -s, -s, s, col, 0.95, W(o, wd)); }
    else {
      const g = 0.55;
      for (const [qx, qy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) line(c, qx * g, qy * g, qx * s, qy * s, col, 0.95, W(o, wd * 0.6));
    }
    c.restore();
  };
  if (k === 'barb') cross(0, 0, 1.9, 1.05);
  else if (k === 'bola') { cross(-2.6, 0, 1.3, 0.9); cross(2.6, 0, 1.3, 0.9); line(c, -1.2, 0.2, 1.2, 0.2, col, 0.8, W(o, 0.6), dash); }
  else if (k === 'siren') {
    cross(0, 0, 1.6, 1);
    arc(c, 0, 0, 3.2, D(-45), D(45), col, 0.9, W(o, 0.8), dash); arc(c, 0, 0, 3.2, D(135), D(225), col, 0.9, W(o, 0.8), dash);
  } else { cross(0, 0, 2.3, 1.3); circle(c, 0, 0, 3.7, col, 0.95, W(o, A ? 1.3 : 0.7), dash); }
  if (o.hostile) {
    const y = k === 'petard' ? 5.4 : 4.3;
    line(c, -3.4, y, 3.4, y, col, 0.95, W(o, 0.8)); line(c, -3.4, y + 1.4, 3.4, y + 1.4, col, 0.95, W(o, 0.8));
  }
};

/** Card 14 Restricted Area: a small point symbol for each kind. */
const restricted: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, w = W(o, 0.8);
  const pt = (x: number, y: number, r: number): void => { if (A) disc(c, x, y, r, col, 1); else circle(c, x, y, r, col, 0.95, w); };
  if (k === 'barb') pt(0, 0, 1.8);
  else if (k === 'bola') { line(c, -1.9, 0, 1.9, 0, col, 0.85, W(o, 0.6)); pt(-1.9, 0, 1.2); pt(1.9, 0, 1.2); }
  else if (k === 'siren') { circle(c, 0, 0, 2.3, col, 0.95, w, A ? [] : [0.8, 0.7]); pt(0, 0, 0.8); }
  else { pt(0, 0, 2.4); circle(c, 0, 0, 3.8, col, 0.95, W(o, A ? 0.9 : 0.6), A ? [] : [0.8, 0.7]); }
  if (o.hostile) {
    const r = k === 'petard' ? 4.8 : k === 'bola' ? 3.8 : 3.2;
    ticks(c, r, r + 2.2, col, 0.95, W(o, 0.8), 8, D(22.5));
  }
};

/** Cards 12 and 14 as one look: the reading picks the card. */
const overprintRestricted: Glyph = (c, k, o) => (o.reading === 'historical' ? overprint : restricted)(c, k, o);

/** Card 30 Living Motion: the kind is how it moves. */
const living: Glyph = (c, k, o) => {
  const col = o.col, A = o.armed, t = o.t, mv = o.motion && A, w = W(o, 0.8);
  if (k === 'barb') { if (A) disc(c, 0, 0, 1.9, col, 1); else circle(c, 0, 0, 1.9, col, 0.95, w); }
  else if (k === 'bola') {
    const a = mv ? t * 1.8 + o.ph * 6 : 0.4;
    for (const q of [0, Math.PI]) {
      const x = Math.cos(a + q) * 2.3, y = Math.sin(a + q) * 2.3;
      if (A) disc(c, x, y, 1.25, col, 1); else circle(c, x, y, 1.25, col, 0.95, w);
    }
  } else if (k === 'siren') {
    if (A) disc(c, 0, 0, 1.3, col, 1); else circle(c, 0, 0, 1.3, col, 0.95, w);
    if (A) {
      const u = mv ? (t * 0.45 + o.ph) % 1 : 0.6;
      circle(c, 0, 0, 1.8 + 3.6 * u, col, 0.85 * (1 - u) + 0.1, W(o, 0.8));
    } else circle(c, 0, 0, 3.6, col, 0.8, W(o, 0.7), [0.9, 0.9]);
  } else {
    const b = mv ? Math.max(0, Math.sin(t * 4.2 + o.ph * 6)) ** 8 * 0.18 : 0;
    if (A) { disc(c, 0, 0, 5, col, 0.14); disc(c, 0, 0, 3 * (1 + b), col, 1); } else circle(c, 0, 0, 3, col, 0.95, W(o, 0.9));
  }
  if (o.hostile) ticks(c, k === 'petard' ? 5.8 : 4.8, (k === 'petard' ? 5.8 : 4.8) + 2, col, 0.95, W(o, 0.9), 4, o.motion ? o.t * 0.7 : 0);
};

/** The glyph of each look. The record is typed by the list, so a look the
 *  list gains without a glyph is a type error. */
export const SEA_MINE_GLYPHS: Record<SeaMineStyle, Glyph> = {
  'overprint-restricted': overprintRestricted,
  'horned-sphere': horned,
  'drogue-lines': drogue,
  'drums-and-grilles': drums,
  'seed-burrs': burrs,
  'side-profile': profile,
  copperplate,
  'danger-circle': dangerCircle,
  'foul-ground': foulGround,
  'living-motion': living,
};

/** The looks that move with the clock or the current. The others hold
 *  still, so one atlas cell per kind, state and side carries them. */
export const MOVING_SEA_MINE_STYLES: ReadonlySet<SeaMineStyle> = new Set<SeaMineStyle>(['drogue-lines', 'living-motion']);

/** Paints one sea mine at the origin of `c` in one look. */
export function paintSeaMine(c: Ctx, style: SeaMineStyle, kind: SeaMineKind, o: SeaMineGlyph): void {
  SEA_MINE_GLYPHS[style](c, kind, o);
}

/** The figures of the generic mine mark (`[presentation.seaobjects]`
 *  `generic_mine_*`, R2902), map px. */
export interface GenericMineFigures {
  r: number;
  spike: number;
  spikes: number;
  width: number;
}

/**
 * The generic mine mark (v2-the-deep.md §11.9 "The canvas", R2902): the
 * one mark of a heard row, a sea mine that a hull at sea alone hears. It
 * is the common base of the sea mine marks with no kind detail: a ring
 * with a dot at its centre and `spikes` short spikes round it, the first
 * one up. It draws at the origin in the ink it is handed; the layer sets
 * the faction ink and the dim alpha on the quad.
 */
export function paintGenericMine(c: Ctx, col: number, f: GenericMineFigures): void {
  circle(c, 0, 0, f.r, col, 1, f.width);
  disc(c, 0, 0, f.r * 0.35, col, 1);
  const n = Math.max(0, Math.round(f.spikes));
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (i / n) * TAU, ca = Math.cos(a), sa = Math.sin(a);
    line(c, ca * f.r, sa * f.r, ca * (f.r + f.spike), sa * (f.r + f.spike), col, 1, f.width);
  }
}

/** The half size of the box of the generic mine mark, map px. */
export function genericMineExtent(f: GenericMineFigures): number {
  return f.r + f.spike + f.width + 1;
}

/** The half size of the box a glyph of one look and kind stays inside, in
 *  map px: the hostile tell is the outermost part. Card 09 reaches the
 *  splash or the contact radius. */
export function seaMineExtent(style: SeaMineStyle, kind: SeaMineKind, contact: number, splash: number): number {
  if (style === 'danger-circle') return Math.max(contact, splash) + 3;
  return kind === 'petard' ? 11 : 10;
}

// ---- the area of a field (cards 12 and 14) --------------------------------------

/** One side of a field of armed sea mines: the own, or a hostile. */
export type SeaMineSide = 'own' | 'foe';

/** What `paintSeaMineArea` is told: the armed sea mines of one side, the
 *  ink of the side, the map zoom and the font stack of the reading. */
export interface SeaMineArea {
  pts: readonly Pt[];
  side: SeaMineSide;
  col: number;
  /** The map zoom: the strokes and the words keep their screen size. */
  z: number;
  /** The font family the reading writes in: the Fell stack for the
   *  historical reading, the mono stack for the modern one. */
  face: string;
}

/** The size of the words of an area in map px at the zoom `z`: the label
 *  step of the chart text in the face of the reading, held inside the
 *  screen band, and 0 under the zoom gate of the step (R3249,
 *  ui-map-interface.md §17.4, R1796). SEA MINES is a warning, and "Mined"
 *  is its Fell twin. */
function areaWordPx(reading: SeaChartReading, z: number): number {
  return bandPx(reading === 'historical' ? 'fell' : 'mono', 'label', z);
}

/**
 * The area the AUTO look draws round the armed sea mines of one side.
 *
 * Card 12 (the historical reading) hatches a hull held 13 px clear of the
 * sea mines, single for an own field and crossed for a hostile one, and
 * writes "Mined" or "Enemy mines" in the italic face. Card 14 (the modern
 * reading and `off`) closes a limit line 12 px clear, with T ticks for an
 * own field and small crosses for a hostile one, and writes SEA MINES on
 * the line. The words of both cards take the label step of the chart text
 * (`areaWordPx`). The reading `off` and a side with fewer than two armed sea
 * mines draw no area.
 * The area rebuilds on a lay, a collect and a detonation.
 */
export function paintSeaMineArea(c: Ctx, reading: SeaChartReading, a: SeaMineArea): void {
  if (a.pts.length < 2 || reading === 'off') return;
  const z = a.z, col = a.col, own = a.side === 'own';
  if (reading === 'historical') {
    const hp = padHull(a.pts, 13);
    c.save(); P(c, hp); c.clip();
    for (let d = -400; d < 400; d += 5) line(c, d - 150, 150, d + 150, -150, col, 0.17, 0.9 / z);
    if (!own) for (let d = -400; d < 400; d += 5) line(c, d - 150, -150, d + 150, 150, col, 0.17, 0.9 / z);
    c.restore();
    P(c, hp); S(c, col, 0.6, 1.2 / z);
    let t = topOf(hp);
    if (!own) for (const q of hp) if (q[1] > t[1]) t = q;
    const wp = areaWordPx(reading, z);
    // The word keeps a gap of 1 screen px over the hull, or 3 under it.
    const dy = own ? -(wp / 2 + 1 / z) : wp / 2 + 3 / z;
    if (wp > 0) text(c, own ? WORDS.area_mined : WORDS.area_enemy_mines, t[0], t[1] + dy, wordFont('fell', wp, a.face), col, 0.85);
    return;
  }
  const hp = padHull(a.pts, 12);
  P(c, hp); Fl(c, col, 0.05); S(c, col, 0.6, 1 / z, [6 / z, 4 / z]);
  walkPoly(hp, 11, (x, y, nx, ny) => {
    if (own) line(c, x, y, x + nx * 3.2, y + ny * 3.2, col, 0.6, 1 / z);
    else {
      const mx = x + nx * 3, my = y + ny * 3, s = 1.3;
      line(c, mx - s, my - s, mx + s, my + s, col, 0.65, 1 / z); line(c, mx + s, my - s, mx - s, my + s, col, 0.65, 1 / z);
    }
  });
  const t = topOf(hp);
  // The word stands 1.5 screen px over the limit line.
  const wp = areaWordPx(reading, z);
  if (wp > 0) text(c, own ? WORDS.area_sea_mines : WORDS.area_hostile_sea_mines, t[0], t[1] - wp / 2 - 1.5 / z, wordFont('mono', wp, a.face), col, 0.8);
}

/** True where a look draws the area of `paintSeaMineArea`: the AUTO look
 *  draws it in the historical and the modern reading. With the reading
 *  `off` it draws the icons of card 14 alone (R2538). */
export function drawsSeaMineArea(style: SeaMineStyle, reading: SeaChartReading): boolean {
  return style === 'overprint-restricted' && reading !== 'off';
}
